import { createHash } from 'crypto';

// Symbol mappings
const SYMBOL_TO_BINANCE_MAP: Record<string, string> = {
  SOL: 'SOLUSDT',
  BTC: 'BTCUSDT',
  ETH: 'ETHUSDT',
  // Add more as needed
};

const SYMBOL_TO_COINGECKO_ID_MAP: Record<string, string> = {
  SOL: 'solana',
  BTC: 'bitcoin',
  ETH: 'ethereum',
  // Add more as needed
};

const SYMBOL_TO_MINT_MAP: Record<string, string> = {
  SOL: 'So11111111111111111111111111111112',
  // Add more as needed
};

// Cache entry interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// Rate limit tracker
interface RateLimit {
  requests: number;
  windowStart: number;
  limit: number;
  windowMs: number;
}

// Cache class
class Cache {
  private cache = new Map<string, CacheEntry<any>>();

  set<T>(key: string, data: T, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }
}

// Rate limiter class
class RateLimiter {
  private limits = new Map<string, RateLimit>();

  constructor(private defaultLimit: number = 100, private defaultWindowMs: number = 60000) {}

  canMakeRequest(source: string): boolean {
    const limit = this.limits.get(source);
    if (!limit) {
      this.limits.set(source, {
        requests: 0,
        windowStart: Date.now(),
        limit: this.defaultLimit,
        windowMs: this.defaultWindowMs,
      });
      return true;
    }

    const now = Date.now();
    if (now - limit.windowStart > limit.windowMs) {
      limit.requests = 0;
      limit.windowStart = now;
    }

    return limit.requests < limit.limit;
  }

  recordRequest(source: string): void {
    const limit = this.limits.get(source);
    if (limit) {
      limit.requests++;
    }
  }
}

// API response interfaces
interface BinanceTicker {
  symbol: string;
  priceChange: string;
  priceChangePercent: string;
  weightedAvgPrice: string;
  prevClosePrice: string;
  lastPrice: string;
  lastQty: string;
  bidPrice: string;
  bidQty: string;
  askPrice: string;
  askQty: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  volume: string;
  quoteVolume: string;
  openTime: number;
  closeTime: number;
  firstId: number;
  lastId: number;
  count: number;
}

interface CoinCapAsset {
  id: string;
  rank: string;
  symbol: string;
  name: string;
  supply: string;
  maxSupply: string;
  marketCapUsd: string;
  volumeUsd24Hr: string;
  priceUsd: string;
  changePercent24Hr: string;
  vwap24Hr: string;
}

interface JupiterPrice {
  id: string;
  type: string;
  price: string;
}

interface DexScreenerToken {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  tags: string[];
  logoURI: string;
  daily_volume: number;
  price: number;
  priceChange: {
    h1: number;
    h6: number;
    h24: number;
  };
}

interface CoinPaprikaTicker {
  id: string;
  name: string;
  symbol: string;
  rank: number;
  circulating_supply: number;
  total_supply: number;
  max_supply: number;
  beta_value: number;
  first_data_at: string;
  last_updated: string;
  quotes: {
    USD: {
      price: number;
      volume_24h: number;
      volume_24h_change_24h: number;
      market_cap: number;
      market_cap_change_24h: number;
      percent_change_15m: number;
      percent_change_30m: number;
      percent_change_1h: number;
      percent_change_6h: number;
      percent_change_12h: number;
      percent_change_24h: number;
      percent_change_7d: number;
      percent_change_30d: number;
      percent_change_1y: number;
      ath_price: number;
      ath_date: string;
      percent_from_price_ath: number;
    };
  };
}

// Main aggregator class
export class MarketDataAggregator {
  private cache = new Cache();
  private rateLimiter = new RateLimiter();
  private sources = ['binance', 'coincap', 'jupiter', 'dexscreener', 'coinpaprika'] as const;
  private logger = console; // Could be replaced with a proper logger

  private async fetchWithRetry(url: string, options: RequestInit = {}, retries = 3): Promise<any> {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      } catch (error) {
        this.logger.warn(`Request failed (attempt ${i + 1}/${retries}): ${error}`);
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000)); // Exponential backoff
        }
      }
    }
    throw new Error(`Failed to fetch after ${retries} retries`);
  }

  private getCacheKey(source: string, method: string, params: any): string {
    const paramsHash = createHash('md5').update(JSON.stringify(params)).digest('hex');
    return `${source}:${method}:${paramsHash}`;
  }

  private async getFromSource<T>(
    source: string,
    method: string,
    params: any,
    fetchFn: () => Promise<T>,
    ttl: number
  ): Promise<T | null> {
    if (!this.rateLimiter.canMakeRequest(source)) {
      this.logger.warn(`Rate limit exceeded for ${source}`);
      return null;
    }

    const cacheKey = this.getCacheKey(source, method, params);
    const cached = this.cache.get<T>(cacheKey);
    if (cached) return cached;

    try {
      this.rateLimiter.recordRequest(source);
      const data = await fetchFn();
      this.cache.set(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.logger.error(`Failed to fetch from ${source}: ${error}`);
      return null;
    }
  }

  async getCryptoPrices(symbols: string[]): Promise<Record<string, number>> {
    const results: Record<string, number> = {};

    for (const symbol of symbols) {
      let price: number | null = null;

      // Try sources in priority order
      for (const source of this.sources) {
        try {
          switch (source) {
            case 'binance':
              const binanceSymbol = SYMBOL_TO_BINANCE_MAP[symbol];
              if (binanceSymbol) {
                const data = await this.getFromSource(
                  source,
                  'ticker',
                  { symbol: binanceSymbol },
                  () => this.fetchWithRetry(`https://api.binance.com/api/v3/ticker/24hr?symbol=${binanceSymbol}`),
                  60000 // 60s TTL
                );
                if (data && data.lastPrice) {
                  price = parseFloat(data.lastPrice);
                }
              }
              break;

            case 'coincap':
              const coinId = SYMBOL_TO_COINGECKO_ID_MAP[symbol];
              if (coinId) {
                const data = await this.getFromSource(
                  source,
                  'assets',
                  { id: coinId },
                  () => this.fetchWithRetry(`https://api.coincap.io/v2/assets/${coinId}`),
                  60000
                );
                if (data && data.data && data.data.priceUsd) {
                  price = parseFloat(data.data.priceUsd);
                }
              }
              break;

            case 'jupiter':
              const mint = SYMBOL_TO_MINT_MAP[symbol];
              if (mint) {
                const data = await this.getFromSource(
                  source,
                  'price',
                  { ids: mint },
                  () => this.fetchWithRetry(`https://price.jup.ag/v6/price?ids=${mint}`),
                  60000
                );
                if (data && data.data && data.data[mint] && data.data[mint].price) {
                  price = parseFloat(data.data[mint].price);
                }
              }
              break;

            case 'dexscreener':
              const data = await this.getFromSource(
                source,
                'tokens',
                { symbol },
                () => this.fetchWithRetry(`https://api.dexscreener.com/latest/dex/tokens/${symbol}`),
                60000
              );
              if (data && data.pairs && data.pairs[0] && data.pairs[0].priceUsd) {
                price = parseFloat(data.pairs[0].priceUsd);
              }
              break;

            case 'coinpaprika':
              const paprikaId = SYMBOL_TO_COINGECKO_ID_MAP[symbol];
              if (paprikaId) {
                const data = await this.getFromSource(
                  source,
                  'tickers',
                  { id: paprikaId },
                  () => this.fetchWithRetry(`https://api.coinpaprika.com/v1/tickers/${paprikaId}`),
                  60000
                );
                if (data && data.quotes && data.quotes.USD && data.quotes.USD.price) {
                  price = data.quotes.USD.price;
                }
              }
              break;
          }

          if (price !== null) break;
        } catch (error) {
          this.logger.warn(`Source ${source} failed for ${symbol}: ${error}`);
          continue;
        }
      }

      if (price !== null) {
        results[symbol] = price;
      }
    }

    return results;
  }

  async getHistoricalPrices(symbol: string, days: number): Promise<Array<{ timestamp: number; price: number }>> {
    // Simplified implementation - in reality, would aggregate from multiple sources
    const binanceSymbol = SYMBOL_TO_BINANCE_MAP[symbol];
    if (!binanceSymbol) return [];

    try {
      const data = await this.getFromSource(
        'binance',
        'klines',
        { symbol: binanceSymbol, interval: '1d', limit: days },
        () => this.fetchWithRetry(`https://api.binance.com/api/v3/klines?symbol=${binanceSymbol}&interval=1d&limit=${days}`),
        300000 // 5min TTL
      );

      if (data) {
        return data.map((kline: any[]) => ({
          timestamp: kline[0],
          price: parseFloat(kline[4]), // Close price
        }));
      }
    } catch (error) {
      this.logger.error(`Failed to get historical prices for ${symbol}: ${error}`);
    }

    return [];
  }

  async getVolumeData(symbol: string, days: number): Promise<Array<{ timestamp: number; volume: number }>> {
    // Similar to historical prices
    const binanceSymbol = SYMBOL_TO_BINANCE_MAP[symbol];
    if (!binanceSymbol) return [];

    try {
      const data = await this.getFromSource(
        'binance',
        'klines',
        { symbol: binanceSymbol, interval: '1d', limit: days },
        () => this.fetchWithRetry(`https://api.binance.com/api/v3/klines?symbol=${binanceSymbol}&interval=1d&limit=${days}`),
        300000
      );

      if (data) {
        return data.map((kline: any[]) => ({
          timestamp: kline[0],
          volume: parseFloat(kline[5]), // Volume
        }));
      }
    } catch (error) {
      this.logger.error(`Failed to get volume data for ${symbol}: ${error}`);
    }

    return [];
  }

  async getTechnicalAnalysis(symbol: string): Promise<{
    rsi: number;
    macd: { signal: number; histogram: number };
    sma20: number;
    sma50: number;
  }> {
    const historical = await this.getHistoricalPrices(symbol, 50);
    if (historical.length < 50) return { rsi: 0, macd: { signal: 0, histogram: 0 }, sma20: 0, sma50: 0 };

    const prices = historical.map(h => h.price);

    // Simple RSI calculation (simplified)
    const gains = prices.slice(1).map((price, i) => Math.max(price - prices[i], 0));
    const losses = prices.slice(1).map((price, i) => Math.max(prices[i] - price, 0));
    const avgGain = gains.reduce((a, b) => a + b, 0) / gains.length;
    const avgLoss = losses.reduce((a, b) => a + b, 0) / losses.length;
    const rs = avgGain / avgLoss;
    const rsi = 100 - (100 / (1 + rs));

    // SMA calculations
    const sma20 = prices.slice(-20).reduce((a, b) => a + b, 0) / 20;
    const sma50 = prices.slice(-50).reduce((a, b) => a + b, 0) / 50;

    // MACD (very simplified)
    const ema12 = this.calculateEMA(prices, 12);
    const ema26 = this.calculateEMA(prices, 26);
    const macdLine = ema12 - ema26;
    const signalLine = this.calculateEMA([macdLine], 9);
    const histogram = macdLine - signalLine;

    return {
      rsi,
      macd: { signal: signalLine, histogram },
      sma20,
      sma50,
    };
  }

  private calculateEMA(prices: number[], period: number): number {
    const multiplier = 2 / (period + 1);
    let ema = prices[0];
    for (let i = 1; i < prices.length; i++) {
      ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
    }
    return ema;
  }
}